import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { researchAreas } from '../data/research';
import { easeOutStrong, fadeUp } from '../utils/motion';
export function ResearchAreasPage() {
  const scrollToArea = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <>
      <section className="bg-white border-b border-line pt-32 pb-20 lg:pt-44 lg:pb-24" data-id="element-2277" data-mp="112ofs9wbzptx">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: easeOutStrong
      }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-2278" data-mp="w4d5xz61dfbp">
          <div className="lg:col-span-5" data-id="element-2279" data-mp="2lk2t03rqdhqx">
            <p className="text-sm font-medium text-teal mb-5" data-id="element-2280" data-mp="2hj10s6w8e5yg">Research Areas</p>
            <h1 className="font-heading font-extrabold text-5xl lg:text-6xl text-ink-dark leading-[1.05] tracking-tight" data-id="element-2281" data-mp="2hgwn4x8j5skz">
              The questions behind SyNeural’s products
            </h1>
          </div>
          <div className="lg:col-span-7 lg:pt-10" data-id="element-2282" data-mp="s35uy4y0mfmd">
            <p className="text-2xl text-ink-dark leading-relaxed mb-6" data-id="element-2283" data-mp="e72edeg2e5sd">
              SyNeural studies how people develop sound judgment, how
              researchers connect ideas, and how institutions turn knowledge
              into responsible action.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-2284" data-mp="1awjeldwa2zmo">
              The areas below preserve the research agenda behind CJI and NCJI
              while expanding it to include the Semantic Knowledge Platform
              (SKP). They describe work to explore, test, and refine—not claims
              that every proposed measure or application has already been
              validated.
            </p>
          </div>
        </motion.div>
      </section>

      <section className="bg-surface-bg py-20 lg:py-28" data-id="element-2285" data-mp="1tocbhjegx4zo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-2286" data-mp="23h6s2k0xv781">
          <nav aria-label="Research areas" className="hidden lg:block lg:col-span-4" data-id="element-2287" data-mp="1zho6dm1ams45">
            <div className="sticky top-28" data-id="element-2288" data-mp="1ugif7sna7cwq">
              <p className="text-sm font-medium text-ink-muted mb-4" data-id="element-2289" data-mp="1pip5wqqom2s8">
                {researchAreas.length} areas
              </p>
              <ul className="border-l border-line" data-id="element-2290" data-mp="1kjvvj76oyrwa">
                {researchAreas.map(area => <li key={area.id} data-id="element-2291" data-mp="1fruktpp6998d">
                    <a href={`#${area.id}`} onClick={scrollToArea(area.id)} className="block -ml-px border-l-2 border-transparent hover:border-navy pl-4 py-1.5 text-sm text-ink-muted hover:text-navy transition-colors duration-150" data-id="element-2292" data-mp="1fqsw4if8go86">
                      {area.title}
                    </a>
                  </li>)}
              </ul>
            </div>
          </nav>

          <div className="lg:col-span-8" data-id="element-2293" data-mp="mpbai20dl2zd">
            {researchAreas.map(area => <motion.article key={area.id} id={area.id} {...fadeUp} className="scroll-mt-28 border-t border-line py-12 first:border-t-0 first:pt-0" data-id="element-2294" data-mp="roeedj4md35r">
                <h2 className="font-heading font-bold text-3xl text-ink-dark tracking-tight mb-5" data-id="element-2295" data-mp="11mlvq32tln8a">
                  {area.title}
                </h2>
                <p className="text-xl text-ink-dark leading-relaxed mb-5" data-id="element-2296" data-mp="8y6hvjvfdayj">{area.lead}</p>
                <div className="space-y-4" data-id="element-2297" data-mp="dy2o7prlheh9">
                  {area.paragraphs.map(paragraph => <p key={paragraph} className="text-lg text-ink-muted leading-relaxed" data-id="element-2298" data-mp="io9b9l6gkqxe">
                      {paragraph}
                    </p>)}
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white py-20 lg:py-28" data-id="element-2299" data-mp="3mb9n1c5bmu2d">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2300" data-mp="1ry5ow7uagt5l">
          <h2 className="font-heading font-bold text-4xl lg:text-5xl tracking-tight mb-6" data-id="element-2301" data-mp="1mzm1e301r9b4">
            From research to responsible application
          </h2>
          <p className="text-xl text-white/80 leading-relaxed mb-10" data-id="element-2302" data-mp="1wwqruf0xdqxq">
            SyNeural’s work connects three activities: organizing knowledge,
            developing human capability, and improving institutional decisions.
            SKP helps connect and govern what is known. CJI and NCJI explore how
            people develop judgment. Pilots and independent review test whether
            the resulting tools work as intended.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2303" data-mp="2fnrpmzqb7gfp">
            <a href="/research" className="inline-flex items-center justify-center gap-2 bg-white text-navy hover:bg-surface-muted font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-2304" data-mp="q93cgw9w9ugm">
              Explore the Research Agenda
              <ArrowRight size={18} data-id="element-2305" data-mp="31u2in1qe6hwj" />
            </a>
            <a href="/partnerships" className="inline-flex items-center justify-center border border-white/40 text-white hover:bg-white/10 font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-2306" data-mp="2n6hf56plugo3">
              Discuss a Research Partnership
            </a>
          </div>
        </motion.div>
      </section>
    </>;
}